import { CentralAdminEntityType } from "Enums/CentralAdminEntityType";
import { Constants } from "Shared/Constants";
import { GetScopeSearchRequest, ScopeItem } from "../Scopes.types";
import { getSelectedEnvironment } from "./Substrate/useSubstrateEnvironmentRingTopologyService";
import { convertResultToScopeItems } from "./scopeSearchResultUtils";
import { targetTypeToString } from "../ScopeSearch.types";
import { useORPCClient } from "Core/oRPC";

export const useScopeSearch = (): {
    getScopeSearchResult: (request: GetScopeSearchRequest, validTargetTypes: CentralAdminEntityType[]) => Promise<ScopeItem[]>;
} => {
    const { inventoryApi } = useORPCClient();
    return {
        getScopeSearchResult: async (request: GetScopeSearchRequest, validTargetTypes: CentralAdminEntityType[]) => {
            const environment = getSelectedEnvironment(request.selectedScopesSoFar || []);
            const workload = request.context?.Workload ?? Constants.defaultWorkload;

            if (!request.searchText.trim()) {
                return [];
            }

            const searchRequests = validTargetTypes.map((targetType) => ({
                Environment: environment,
                Workload: workload,
                TargetType: targetType,
                TargetValue: request.searchText.trim().toUpperCase(),
                TargetTypeString: targetTypeToString[targetType]
            }));

            for (const searchRequest of searchRequests) {
                try {
                    const result = await Promise.race([
                        inventoryApi.postTopology(searchRequest),
                        new Promise((_, reject) => setTimeout(() => reject(new Error("Request timed out")), 5000))
                    ]);
                    if (result) {
                        const resultScopeItems = convertResultToScopeItems(result, searchRequest.TargetType, request.scopeHierarchyId);
                        const hasEnvironmentScope = resultScopeItems.some((item) => item.ScopeType === "Environment" && item.Value);
                        const hasRingScope = resultScopeItems.some((item) => item.ScopeType === "Ring" && item.Value);
                        const hasNotFoundScopeItems = resultScopeItems.some((item) => item.Value?.toLocaleLowerCase() === "notfound");
                        if (hasNotFoundScopeItems) {
                            return [];
                        }
                        if (resultScopeItems.length > 0) {
                            if (!hasEnvironmentScope) {
                                resultScopeItems.push({
                                    ScopeType: "Environment",
                                    Value: "Prod"
                                });
                            }
                            if (hasRingScope) {
                                resultScopeItems.push({
                                    ScopeType: "Ring",
                                    Value: "All"
                                });
                            }
                            return resultScopeItems;
                        }
                    }
                } catch (error) {
                    // Ignore the error and continue with the next request
                }
            }

            return [];
        }
    };
};
